'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, 
  XAxis, YAxis, Tooltip, CartesianGrid , Legend, ReferenceLine
} from 'recharts';
import { 
  Layers, Clock, Cpu, BarChart2, Activity, 
  Filter, Check, RefreshCw, Database, TrendingUp, Sliders
} from 'lucide-react';
import { colors, baseStyles, formatNum, formatUSD } from './SharedUI';

const LOOKBACK_OPTIONS = [
  { id: 1, label: '1 Ngày (24h)' },
  { id: 2, label: '2 Ngày (48h)' },
  { id: 3, label: '3 Ngày (72h)' },
  { id: 4, label: '4 Ngày (96h)' },
  { id: 5, label: '5 Ngày (120h)' },
];

const MATRIX_TYPES = [
  { id: 'VOLUME', label: 'Tổng Volume', color: '#f59e0b' },
  { id: 'BUY', label: 'Taker Buy Vol', color: '#089981' },
  { id: 'SELL', label: 'Taker Sell Vol', color: '#f23645' },
];

export default function AdvancedOrderflowMatrix({ symbol: propSymbol = 'BTCUSDT', smartMoneyRef, liveDataRef }) {
  // Trạng thái lưu trữ giá trị đang gõ trên ô Input
  const [inputSymbol, setInputSymbol] = useState(propSymbol);
  
  // Trạng thái lưu trữ mã thực tế đang được hiển thị trên ma trận
  const [activeSymbol, setActiveSymbol] = useState('');
  
  const [lookbackDays, setLookbackDays] = useState(3); 
  const [matrixType, setMatrixType] = useState('VOLUME'); 
  const [currency, setCurrency] = useState('USDT');

  // Bộ lọc chỉ báo kỹ thuật hiển thị động trên Chart giá
  const [indicators, setIndicators] = useState({
    close: true,
    sma20: false, sma50: false, sma200: false,
    sma7: true, sma25: true, sma99: true,
    ema9: false, ema21: false, ema50: false,
    bb: true, rsi: true, macd: true,
    vwap: true
  });

  // 🛠️ THÊM MỚI: Bộ lọc 5 tính năng tối thượng của Footprint Chart (Mặc định bật toàn bộ)
  const [footprintFilters, setFootprintFilters] = useState({
    splitBidAsk: true,    // Bẻ đôi cột hiển thị lực Mua/Bán chủ động
    highlightPOC: true,   // Bo viền phát sáng vùng giá tổ chức gom nhiều nhất
    imbalanceBox: true,   // Nhuộm sắc độ màu Neon rực khi quét lệnh mạnh
    deltaLabel: true,     // In chữ số Delta cụ thể vào trong tầng giá
    exhaustionSpot: true  // Đánh dấu vùng kiệt sức sụt giảm Vol ở biên đỉnh/đáy
  });

  const [rawKlines, setRawKlines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasSearched] = useState(false);

  // 🛠️ ĐÃ CẬP NHẬT: Đồng bộ hóa khi component cha truyền vào một symbol mới thay đổi
  useEffect(() => {
    if (propSymbol) {
      const cleanSymbol = propSymbol.trim().toUpperCase();
      setInputSymbol(cleanSymbol);
      // Giữ nguyên nguyên tắc "Lazy Fetch": Không tự động kích hoạt tải dữ liệu ngay, 
      // chỉ reset trạng thái hiển thị cũ để chờ user bấm Fetch mới.
      setHasSearched(false);
      setRawKlines([]);
      setActiveSymbol('');
    }
  }, [propSymbol]);

  // 1. HÀM CHỦ ĐỘNG FETCH DATA (CHỈ CHẠY KHI ẤN NÚT)
  const handleFetchMatrix = async () => {
    if (!inputSymbol.trim()) return;
    const targetCoin = inputSymbol.trim().toUpperCase();
    
    setIsLoading(true);
    setHasSearched(false);

    // Tính toán số nến 1H cần lấy (1 ngày = 24 nến)
    const limit = Math.max(lookbackDays * 24, 250);

    try {
      const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${targetCoin}&interval=1h&limit=${limit}`);
      if (!res.ok) throw new Error('Không tìm thấy mã Token Futures này hoặc Binance quá tải');
      
      const data = await res.json();
      setRawKlines(data);
      setActiveSymbol(targetCoin);
      setHasSearched(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleIndicator = (key) => {
    setIndicators(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFootprintFilter = (key) => {
    setFootprintFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // =========================================================================
  // 2. BỘ MÁY TOÁN HỌC TÍNH TOÁN CHỈ BÁO KỸ THUẬT & PROFILE (GLOBAL SCOPE ANALYZER)
  // =========================================================================
  const processedChartData = useMemo(() => {
    if (rawKlines.length === 0) return [];

    const len = rawKlines.length;
    const closes = rawKlines.map(k => parseFloat(k[4]));

    const calcSMA = (period) => {
      const arr = new Array(len).fill(null);
      for (let i = period - 1; i < len; i++) {
        arr[i] = closes.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0) / period;
      }
      return arr;
    };

    const calcEMA = (period) => {
      const arr = new Array(len).fill(null);
      if (len < period) return arr;
      const smoothing = 2 / (period + 1);
      const initSum = closes.slice(0, period).reduce((a, b) => a + b, 0);
      arr[period - 1] = initSum / period;
      for (let i = period; i < len; i++) {
        arr[i] = closes[i] * smoothing + arr[i - 1] * (1 - smoothing);
      }
      return arr;
    };

    const sma20 = calcSMA(20);
    const sma50 = calcSMA(50);
    const sma200 = calcSMA(200);
    const sma7 = calcSMA(7);
    const sma25 = calcSMA(25);
    const sma99 = calcSMA(99);
    const ema9 = calcEMA(9);
    const ema21 = calcEMA(21);
    const ema50 = calcEMA(50);

    const rsiArr = new Array(len).fill(null);
    const rsiPeriod = 14;
    if (len > rsiPeriod) {
      let g = 0, l = 0;
      for (let i = 1; i <= rsiPeriod; i++) {
        const d = closes[i] - closes[i - 1];
        if (d > 0) g += d; else l -= d;
      }
      let avgG = g / rsiPeriod;
      let avgL = l / rsiPeriod;
      rsiArr[rsiPeriod] = avgL === 0 ? 100 : 100 - (100 / (1 + avgG / avgL));
      for (let i = rsiPeriod + 1; i < len; i++) {
        const d = closes[i] - closes[i - 1];
        avgG = (avgG * 13 + (d > 0 ? d : 0)) / 14;
        avgL = (avgL * 13 + (d < 0 ? -d : 0)) / 14;
        rsiArr[i] = avgL === 0 ? 100 : 100 - (100 / (1 + avgG / avgL));
      }
    }

    const macdLine = new Array(len).fill(null);
    const signalLine = new Array(len).fill(null);
    const macdHist = new Array(len).fill(null);
    const ema12 = calcEMA(12);
    const ema26 = calcEMA(26);
    for (let i = 25; i < len; i++) {
      if (ema12[i] && ema26[i]) macdLine[i] = ema12[i] - ema26[i];
    }
    let fIdx = macdLine.findIndex(v => v !== null);
    if (fIdx !== -1 && len >= fIdx + 9) {
      const sIdx = fIdx + 8;
      signalLine[sIdx] = macdLine.slice(fIdx, sIdx + 1).reduce((a, b) => a + b, 0) / 9;
      for (let i = sIdx + 1; i < len; i++) {
        if (macdLine[i] && signalLine[i - 1]) signalLine[i] = macdLine[i] * 0.2 + signalLine[i - 1] * 0.8;
      }
      for (let i = sIdx; i < len; i++) {
        if (macdLine[i] && signalLine[i]) macdHist[i] = macdLine[i] - signalLine[i];
      }
    }

    const bbUpper = new Array(len).fill(null);
    const bbLower = new Array(len).fill(null);
    for (let i = 19; i < len; i++) {
      const m = sma20[i];
      const vr = closes.slice(i - 19, i + 1).reduce((a, b) => a + Math.pow(b - m, 2), 0) / 20;
      const sd = Math.sqrt(vr);
      bbUpper[i] = m + (2 * sd);
      bbLower[i] = m - (2 * sd);
    }

    // =========================================================================
    // THUẬT TOÁN TÍNH DAILY VWAP (RESET MỖI KHI SANG NGÀY MỚI THEO UTC/BINANCE)
    // =========================================================================
    const vwapArr = new Array(len).fill(null);
    let currentDayStr = "";
    let sumVolumePrice = 0;
    let sumVolume = 0;

    for (let i = 0; i < len; i++) {
      const k = rawKlines[i];
      const timestamp = k[0];
      const dObj = new Date(timestamp);
      // Lấy chuỗi ngày (Ví dụ: "2026-06-25") để nhận biết thời điểm chuyển phiên ngày mới
      const dayStr = dObj.toISOString().split('T')[0]; 

      const h = parseFloat(k[2]) || 0;
      const l = parseFloat(k[3]) || 0;
      const c = parseFloat(k[4]) || 0;
      const v = parseFloat(k[5]) || 0;

      // Giá điển hình (Typical Price) của cây nến
      const typicalPrice = (h + l + c) / 3;

      // Nếu chuyển sang ngày mới, tiến hành reset toàn bộ bộ đếm tích lũy về 0
      if (dayStr !== currentDayStr) {
        currentDayStr = dayStr;
        sumVolumePrice = 0;
        sumVolume = 0;
      }

      sumVolumePrice += typicalPrice * v;
      sumVolume += v;

      vwapArr[i] = sumVolume > 0 ? sumVolumePrice / sumVolume : c;
    }
    // =========================================================================

    return rawKlines.map((k, idx) => {
      const ms = k[0];
      const dObj = new Date(ms);
      const vol = parseFloat(k[5]) || 0;
      const tBuyVol = parseFloat(k[9]) || 0;
      const tSellVol = Math.max(0, vol - tBuyVol);

      return {
        ms,
        timeLabel: dObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dateLabel: dObj.toLocaleDateString([], { month: '2-digit', day: '2-digit' }),
        open: parseFloat(k[1]),
        high: parseFloat(k[2]),
        low: parseFloat(k[3]),
        close: closes[idx],
        volume: vol,
        takerBuyVol: tBuyVol,
        takerSellVol: tSellVol,
        dayName: dObj.toLocaleDateString('en-US', { weekday: 'long' }),
        hourIndex: dObj.getHours(),
        sma20: sma20[idx], sma50: sma50[idx], sma200: sma200[idx],
        sma7: sma7[idx], sma25: sma25[idx], sma99: sma99[idx],
        ema9: ema9[idx], ema21: ema21[idx], ema50: ema50[idx],
        rsi: rsiArr[idx], macdHist: macdHist[idx],
        bbUpper: bbUpper[idx], bbLower: bbLower[idx],
        vwap: vwapArr[idx]
      };
    });
  }, [rawKlines]);
    // MẢNG HIỂN THỊ CẮT LỌC RIÊNG CHO CHART GIÁ + CLUSTERED BAR CHART THEO RANGE DAYS NGƯỜI DÙNG CHỌN
  const visibleChartData = useMemo(() => {
    if (processedChartData.length === 0) return [];
    const neededCandles = lookbackDays * 24;
    return processedChartData.slice(-neededCandles);
  }, [processedChartData, lookbackDays]);

  // =========================================================================
  // 3. THUẬT TOÁN PHÂN PHỐI KHỐI LƯỢNG NGANG (VOLUME PROFILE)
  // =========================================================================
  const volumeProfileData = useMemo(() => {
    if (processedChartData.length === 0) return [];
    const prices = processedChartData.map(b => b.close);
    const maxP = Math.max(...prices);
    const minP = Math.min(...prices);
    const totalBins = 14; 
    const binSize = (maxP - minP) / totalBins;

    const bins = Array.from({ length: totalBins }, (_, i) => {
      const low = minP + (i * binSize);
      const high = low + binSize;
      return {
        priceLevel: ((low + high) / 2).toFixed(2),
        volume: 0, low, high
      };
    });

    processedChartData.forEach(bar => {
      for (let i = 0; i < totalBins; i++) {
        if (bar.close >= bins[i].low && bar.close <= bins[i].high) {
          bins[i].volume += bar.volume;
          break;
        }
      }
    });
    return bins;
  }, [processedChartData]);

  // =========================================================================
  // 4. THUẬT TOÁN ĐỒNG BỘ MA TRẬN NHIỆT 24/7 (CRYPTO ORDERFLOW HEATMAP)
  // =========================================================================
  const heatmapMatrix = useMemo(() => {
    if (processedChartData.length === 0) return null;

    const daysEn = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const daysVn = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'CN'];
    const tradingHours = Array.from({ length: 24 }, (_, i) => i);

    const matrix = {};
    daysEn.forEach(d => {
      matrix[d] = {};
      tradingHours.forEach(h => { matrix[d][h] = 0; });
    });

    const targetN = lookbackDays * 24;
    const dataForHeatmap = processedChartData.slice(-targetN);

    dataForHeatmap.forEach(bar => {
      if (matrix[bar.dayName] && matrix[bar.dayName][bar.hourIndex] !== undefined) {
        let valueToAdd = bar.volume;
        if (matrixType === 'BUY') valueToAdd = bar.takerBuyVol;
        if (matrixType === 'SELL') valueToAdd = bar.takerSellVol;

        matrix[bar.dayName][bar.hourIndex] += valueToAdd;
      }
    });

    let maxVal = 1;
    daysEn.forEach(d => {
      tradingHours.forEach(h => {
        if (matrix[d][h] > maxVal) maxVal = matrix[d][h];
      });
    });

    return { matrix, daysEn, daysVn, tradingHours, maxVolumeInGrid: maxVal };
  }, [processedChartData, matrixType, lookbackDays]);

  // 🛠️ THÊM MỚI: THUẬT TOÁN PHÂN RÃ TẦNG GIÁ TRONG RUỘT NẾN PHỤC VỤ FOOTPRINT BAR CHART MATRIX
  const footprintChartData = useMemo(() => {
    if (visibleChartData.length === 0) return [];

    // Chạy qua từng cây nến 1H trong tập hiển thị để giả lập bóc tách 5 tầng giá trong ruột nến
    return visibleChartData.map(bar => {
      const deltaTotal = bar.takerBuyVol - bar.takerSellVol;
      const range = bar.high - bar.low || 1;
      const step = range / 5;

      // Chia nến thành 5 tầng giá từ thấp lên cao
      const clusters = Array.from({ length: 5 }, (_, i) => {
        const floorPrice = bar.low + (i * step);
        const ceilPrice = floorPrice + step;
        const midPrice = (floorPrice + ceilPrice) / 2;

        // Phân bổ ngẫu nhiên khối lượng dựa trên phân phối hình chuông để sinh dữ liệu Bid/Ask Snapshot
        const weight = [0.1, 0.25, 0.4, 0.18, 0.07][i]; 
        const clusterVol = bar.volume * weight;
        const clusterBuy = bar.takerBuyVol * weight;
        const clusterSell = Math.max(0, clusterVol - clusterBuy);
        const clusterDelta = clusterBuy - clusterSell;

        return {
          priceLevel: midPrice.toFixed(2),
          bid: clusterSell,
          ask: clusterBuy,
          delta: clusterDelta,
          total: clusterVol
        };
      });

      // Xác định tầng giá nào là POC (Có tổng Vol lớn nhất)
      let maxClusterVol = -1;
      let pocIndex = 2;
      clusters.forEach((c, idx) => {
        if (c.total > maxClusterVol) {
          maxClusterVol = c.total;
          pocIndex = idx;
        }
      });

      return {
        ...bar,
        deltaTotal,
        clusters,
        pocIndex, // Lưu vị thế tầng giá để phục vụ highlight bộ lọc POC
      };
    });
  }, [visibleChartData]);

  // =========================================================================
  // THUẬT TOÁN TÍNH OHLC TỔNG CHU KỲ CHẠY ĐỘNG THEO SỐ NGÀY ĐƯỢC CHỌN (1D - 5D)
  // =========================================================================
  const periodOHLC = useMemo(() => {
    if (visibleChartData.length === 0) return { open: 0, high: 0, low: 0, close: 0 };

    const opens = visibleChartData.map(d => d.open);
    const highs = visibleChartData.map(d => d.high);
    const lows = visibleChartData.map(d => d.low);
    const closes = visibleChartData.map(d => d.close);

    return {
      open: opens[0],                           // Giá mở cửa của cây nến cũ nhất chu kỳ
      high: Math.max(...highs),                 // Đỉnh cao nhất toàn chu kỳ số ngày chọn
      low: Math.min(...lows),                   // Đáy thấp nhất toàn chu kỳ số ngày chọn
      close: closes[closes.length - 1]         // Giá đóng cửa hiện tại của thị trường
    };
  }, [visibleChartData]);

     // =========================================================================
  // 🛠️ FIX DỨT ĐIỂM: ÉP KIỂU SỐ THỰC CHO TAKERBUYVOL & TAKERSELLVOL
  // =========================================================================
  const smartMoneyMetrics = useMemo(() => {
    if (visibleChartData.length === 0) return { marketOI: 0, smartOI: 0, pct: 0 };
    
    const currentPrice = parseFloat(periodOHLC.close) || 1;
    const liveSMQuote = smartMoneyRef?.current.totalPositions;
    const liveOiQuote = parseFloat(liveDataRef?.current?.oiQuote || 0);
    console.log('liveSMQuote', liveSMQuote);
    console.log('liveOiQuote', liveOiQuote);


    // Ép kiểu parseFloat để đảm bảo JS không cộng chuỗi bậy bạ hoặc nuốt số 0
    const totalBuyerVol = visibleChartData.reduce((acc, cur) => {
      const buyVol = parseFloat(cur.takerBuyVol) || 0;
      return acc + buyVol;
    }, 0);
    
    const totalSellerVol = visibleChartData.reduce((acc, cur) => {
      const sellVol = parseFloat(cur.takerSellVol) || 0;
      return acc + sellVol;
    }, 0);

    // 1. Tính Market OI = |Total Buyer - Total Seller| / Current Price
    const marketOI = Math.min(totalBuyerVol,totalSellerVol);
    const absoluteNetVol = totalBuyerVol - totalSellerVol;
    

    // 2. Tính Smart Ratio = Total Buyer / Total Seller (Tránh lỗi chia cho 0)
    const smartRatio = totalSellerVol > 0 ? (totalBuyerVol / totalSellerVol) : 1;

    // Quy đổi tỷ lệ phần trăm hiển thị
    const pctDisplay = smartRatio * 100;

    // 3. Quy mô OI dòng tiền của Smart Money
    const smartOI = absoluteNetVol * currentPrice;

    return {
      marketOI,
      smartOI,
      pct: pctDisplay,
      liveSMQuote: liveSMQuote,
      liveOiQuote: liveOiQuote
    };
  }, [visibleChartData, periodOHLC.close]);




  return (
    <div style={{ backgroundColor: colors?.bg || '#0b0e14', color: '#ffffff', padding: '24px', borderRadius: '8px', border: `1px solid ${colors?.border || '#2b3139'}`, maxWidth: '1650px', margin: '0 auto', fontFamily: 'monospace', boxSizing: 'border-box' }}>
      
      {/* KHU VỰC THANH ĐIỀU KHIỂN SÁNG SNAPSHOT (IDLE) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Layers size={24} color="#3b82f6" />
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>ADVANCED ORDERFLOW HEATMAP & PROFILE MATRIX</h1>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors?.textSub || '#848e9c', fontWeight: 'bold' }}>
                Định vị thời gian gom hàng & Vị thế tích lũy khối lượng ngang (Nến 1H)
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input 
              type="text" 
              value={inputSymbol} 
              onChange={(e) => setInputSymbol(e.target.value)}
              placeholder="Mã Token (BTCUSDT)" 
              style={{ ...baseStyles.input, width: '130px', textTransform: 'uppercase', fontWeight: 'bold' }}
              disabled={isLoading}
            />
            <button 
              onClick={handleFetchMatrix} 
              disabled={isLoading}
              style={{ backgroundColor: isLoading ? 'rgba(255,255,255,0.1)' : '#3b82f6', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: '4px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> {isLoading ? 'Quét...' : 'FETCH MATRIX'}
            </button>
          </div>
        </div>

        {/* PHÂN ĐOẠN TÙY CHỌN TĨNH */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.01)', padding: '12px', borderRadius: '6px', border: `1px dashed ${colors?.border || '#2b3139'}` }}>
          
          {/* Chọn số ngày */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 'bold', textTransform: 'uppercase' }}>Tầm Nhìn Quét (Lookback Days):</span>
            <div style={{ display: 'flex', gap: '4px', backgroundColor: 'rgba(0,0,0,0.3)', padding: '3px', borderRadius: '4px' }}>
              {LOOKBACK_OPTIONS.map(opt => (
                <button
                  key={`opt-day-${opt.id}`}
                  onClick={() => setLookbackDays(opt.id)}
                  style={{ backgroundColor: lookbackDays === opt.id ? '#3b82f6' : 'transparent', color: '#ffffff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chọn dạng dòng tiền Heatmap */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 'bold', textTransform: 'uppercase' }}>Cấu Trúc Khối Lượng Heatmap:</span>
            <div style={{ display: 'flex', gap: '4px', backgroundColor: 'rgba(0,0,0,0.3)', padding: '3px', borderRadius: '4px' }}>
              {MATRIX_TYPES.map(type => (
                <button
                  key={`opt-type-${type.id}`}
                  onClick={() => setMatrixType(type.id)}
                  style={{ backgroundColor: matrixType === type.id ? type.color : 'transparent', color: '#ffffff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Hộp bộ lọc nhanh các Chỉ báo Indicator */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: 'auto' }}>
            <span style={{ fontSize: '11px', color: colors?.textSub || '#848e9c', fontWeight: 'bold', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}><Filter size={12}/> Bộ Lọc Chỉ Báo Nền:</span>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {['close','sma20', 'sma50', 'sma200', 'sma7', 'sma25', 'sma99', 'ema9', 'ema21', 'ema50', 'bb', 'rsi', 'macd','vwap'].map(indKey => {
                const active = indicators[indKey];
                return (
                  <button
                    key={`btn-filter-${indKey}`}
                    onClick={() => toggleIndicator(indKey)}
                    style={{ backgroundColor: active ? 'rgba(59, 130, 246, 0.15)' : 'transparent', color: active ? '#60a5fa' : colors?.textSub, border: `1px solid ${active ? '#3b82f6' : colors?.border}`, padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    {indKey.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      {/* 🛠️ THÊM MỚI: BẢNG ĐIỀU KHIỂN BỘ LỌC ĐỘNG CHO 5 TÍNH NĂNG FOOTPRINT */}
      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center', backgroundColor: 'rgba(6, 182, 212, 0.03)', padding: '12px', borderRadius: '6px', border: '1px solid rgba(6, 182, 212, 0.15)' }}>
        <span style={{ fontSize: '11px', color: '#06b6d4', fontWeight: 'bold', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Sliders size={13} /> BỘ LỌC TÍN HIỆU FOOTPRINT (MẶC ĐỊNH BẬT HẾT):
        </span>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'splitBidAsk', label: 'Bẻ Đôi Mua/Bán' },
            { key: 'highlightPOC', label: 'Highlight Vùng POC' },
            { key: 'imbalanceBox', label: 'Hộp Màu Neon Imbalance' },
            { key: 'deltaLabel', label: 'Hiện Chữ Số Delta' },
            { key: 'exhaustionSpot', label: 'Quét Kiệt Sức Đỉnh/Đáy' }
          ].map(filterItem => {
            const active = footprintFilters[filterItem.key];
            return (
              <button
                key={`footprint-filter-${filterItem.key}`}
                onClick={() => toggleFootprintFilter(filterItem.key)}
                style={{ backgroundColor: active ? '#06b6d4' : 'transparent', color: active ? '#000000' : '#06b6d4', border: '1px solid #06b6d4', padding: '5px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                {active ? '✓ ' : '✗ '} {filterItem.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* SNAPSHOT KHUNG TRẠNG THÁI CHỜ KHI CHƯA FETCH */}
      {!hasFetched && (
        <div style={{ border: `1px dashed ${colors?.border || '#2b3139'}`, padding: '60px', textAlign: 'center', borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.15)' }}>
          <Clock size={36} color={colors?.textSub} style={{ margin: '0 auto 16px auto', opacity: 0.5 }} />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 'bold', color: '#ffffff' }}>MA TRẬN HỆ THỐNG ĐANG Ở TRẠNG THÁI CHỜ ĐỒNG BỘ</h3>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: colors?.textSub, lineHeight: '1.5' }}>
            Vui lòng chọn cấu hình Tầm nhìn ngày (1D - 5D) cùng Phân mục dòng tiền phía trên, <br />
            sau đó nhấn nút <strong style={{ color: '#3b82f6' }}>FETCH MATRIX</strong> để bóc tách tệp lệnh nến 1H nguyên bản từ Binance Futures Server.
          </p>
        </div>
      )}

              {/* 🛠️ THÊM MỚI: DASHBOARD THÔNG TIN ĐỘNG OHLC CHU KỲ & SMART MONEY SIGNAL (CHỈ HIỂN THỊ KHI CÓ DATA) */}
        {hasFetched && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginTop: '10px', marginBottom: '10px' }}>
            
            {/* CỤM 1: PERIOD OPEN */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: colors?.textSub || '#848e9c', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>⏱️ Mở Cửa Chu Kỳ</span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: '#ffffff' }}>${periodOHLC.open.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>

            {/* CỤM 2: PERIOD HIGH (MÀU XANH LỤC RỰC) */}
            <div style={{ backgroundColor: 'rgba(8, 153, 129, 0.04)', border: '1px solid rgba(8, 153, 129, 0.2)', borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: '#089981', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>📈 Đỉnh Cao Nhất ({lookbackDays}D)</span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: '#089981' }}>${periodOHLC.high.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>

            {/* CỤM 3: PERIOD LOW (MÀU ĐỎ RỰC) */}
            <div style={{ backgroundColor: 'rgba(242, 54, 69, 0.04)', border: '1px solid rgba(242, 54, 69, 0.2)', borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: '#f23645', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>📉 Đáy Thấp Nhất ({lookbackDays}D)</span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: '#f23645' }}>${periodOHLC.low.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>

            {/* CỤM 4: MARKET OPEN INTEREST */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: colors?.textSub || '#848e9c', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>📊 Market Open Interest (OI)</span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: '#3b82f6' }}>
                ${smartMoneyMetrics.marketOI >= 1000000000 ? `${(smartMoneyMetrics.marketOI).toFixed(2)}` : `${(smartMoneyMetrics.marketOI.toFixed(2))}`}
              </span>
            </div>

            {/* CỤM 5: OI SMART SIGNAL */}
            <div style={{ backgroundColor: 'rgba(192, 132, 252, 0.04)', border: '1px solid rgba(192, 132, 252, 0.2)', borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: '#c084fc', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>🐳 OI Smart Money Signal</span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: smartMoneyMetrics.pct >= 0 ? '#089981' : '#f23645' }}>
               {smartMoneyMetrics.pct >= 0 ? '+' : ''}${smartMoneyMetrics.smartOI >= 1000000000 ? `${(smartMoneyMetrics.smartOI / 1000000000).toFixed(2)}B` : `${(smartMoneyMetrics.smartOI / 1000000).toFixed(2)}M`}
              </span>
            </div>

            {/* CỤM 6: SMART CAPTURE RATIO (%) */}
            <div style={{ backgroundColor: 'rgba(6, 182, 212, 0.04)', border: '1px solid rgba(6, 182, 212, 0.2)', borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: '#06b6d4', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>🎯 Smart Capture Ratio (%)</span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: smartMoneyMetrics.pct >= 0 ? '#089981' : '#f23645' }}>
                {smartMoneyMetrics.pct >= 0 ? '▲' : '▼'} {smartMoneyMetrics.pct >= 0 ? '+' : ''}{smartMoneyMetrics.pct.toFixed(2)}%
              </span>
            </div>

            {/* CỤM 7: SM Quote / Market Quote */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '12px', textAlign: 'left' }}>
              <span style={{ fontSize: '10px', color: colors?.textSub || '#848e9c', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px'}}> SM Quote / Market Quote </span>
              <span style={{ fontSize: '16px', fontWeight: '900', color: '#3b82f6' }}>
                ${formatUSD(smartMoneyMetrics.liveSMQuote)}/${formatUSD(smartMoneyMetrics.liveOiQuote)} -- ${((smartMoneyMetrics.liveSMQuote / smartMoneyMetrics.liveOiQuote) * 100).toFixed(2)}%
              </span>
            </div>

          </div>
        )}
        {/* ========================================================================= */}
        {/* 🛠️ THÊM MỚI: BẢNG TRA CỨU PHIÊN GIAO DỊCH MỸ (EDT) VÀ GIỜ VIỆT NAM (GMT+7) */}
        {/* ========================================================================= */}
        <div style={{ 
          backgroundColor: 'rgba(30, 41, 59, 0.3)', 
          border: `1px solid ${colors?.border || '#2b3139'}`, 
          borderRadius: '8px', 
          padding: '14px', 
          marginTop: '12px', 
          marginBottom: '12px' 
        }}>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 'bold', 
            color: '#38bdf8', 
            marginBottom: '10px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px' 
          }}>
            🌐 KHUNG THỜI GIAN PHIÊN GIAO DỊCH MỸ (EDT) VS GIỜ VIỆT NAM (GMT+7)
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              fontSize: '11px', 
              textAlign: 'left',
              color: '#cbd5e1'
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <th style={{ padding: '8px 10px', fontWeight: 'bold', color: '#94a3b8' }}>Phiên Giao Dịch Mỹ (EDT)</th>
                  <th style={{ padding: '8px 10px', fontWeight: 'bold', color: '#a3e635' }}>Giờ Việt Nam (GMT+7)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', backgroundColor: 'rgba(251, 191, 36, 0.02)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 'bold' }}>🌅 Pre-market</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ color: '#94a3b8' }}>4:00 AM – 9:30 AM</span> 
                    <span style={{ margin: '0 8px', color: '#475569' }}>➔</span> 
                    <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>3:00 PM – 8:30 PM</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', backgroundColor: 'rgba(8, 153, 129, 0.03)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 'bold', color: '#089981' }}>📈 Regular Market (Phiên Chính)</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ color: '#94a3b8' }}>9:30 AM – 4:00 PM</span> 
                    <span style={{ margin: '0 8px', color: '#475569' }}>➔</span> 
                    <span style={{ color: '#089981', fontWeight: 'bold' }}>8:30 PM – 3:00 AM (hôm sau)</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', backgroundColor: 'rgba(192, 132, 252, 0.02)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 'bold' }}>🌙 After Hours</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ color: '#94a3b8' }}>4:00 PM – 8:00 PM</span> 
                    <span style={{ margin: '0 8px', color: '#475569' }}>➔</span> 
                    <span style={{ color: '#c084fc', fontWeight: 'bold' }}>3:00 AM – 7:00 AM</span>
                  </td>
                </tr>
                <tr style={{ backgroundColor: 'rgba(242, 54, 69, 0.02)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 'bold' }}>🌃 Overnight</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ color: '#94a3b8' }}>8:00 PM – 4:00 AM</span> 
                    <span style={{ margin: '0 8px', color: '#475569' }}>➔</span> 
                    <span style={{ color: '#f23645', fontWeight: 'bold' }}>7:00 AM – 3:00 PM</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      {/* KHU VỰC HIỂN THỊ ĐỒ THỊ CHUYÊN SÂU SAU KHI CLICK FETCH */}
      {hasFetched && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* TẦNG THƯỢNG: ĐỐI CHIẾU GIÁ + VOLUME PROFILE NGANG & HEATMAP DỌC */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))', gap: '24px' }}>
            
            {/* CỤM BÊN TRÁI: KHỐI GIÁ VÀ VOLUME PROFILE */}
            <div style={{ border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '900', color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <BarChart2 size={16} /> 1. VERTICAL PRICE INTEGRATION & VOLUME PROFILE ({currency})
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr', gap: '12px', alignItems: 'stretch' }}>
                <div style={{ height: '280px' }}>
                  <ResponsiveContainer>
                    <LineChart data={processedChartData} margin={{ top: 5, right: 5, left: -5, bottom: 5 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.02)" />
                      <XAxis dataKey="dateLabel" tick={{ fill: colors?.textSub, fontSize: 10 }} />
                      <YAxis tick={{ fill: colors?.textSub, fontSize: 10 }} domain={['dataMin', 'dataMax']} />
                      <Tooltip contentStyle={{ background: colors?.bg, borderColor: colors?.border, fontSize: 12, color: '#fff' }} />
                      {indicators.close && <Line type="monotone" dataKey="close" stroke="#fff" strokeWidth={2} dot={false} name="Giá Đóng" />}
                      
                      {indicators.sma20 && <Line type="monotone" dataKey="sma20" stroke="#fbbf24" strokeWidth={1} dot={false} name="SMA 20" />}
                      {indicators.sma50 && <Line type="monotone" dataKey="sma50" stroke="#f59e0b" strokeWidth={1} dot={false} name="SMA 50" />}
                      {indicators.sma200 && <Line type="monotone" dataKey="sma200" stroke="#d97706" strokeWidth={1} dot={false} name="SMA 200" />}
                      {indicators.sma7 && <Line type="monotone" dataKey="sma7" stroke="#fbbf54" strokeWidth={1} dot={false} name="SMA 7" />}
                      {indicators.sma25 && <Line type="monotone" dataKey="sma25" stroke="#f59e3b" strokeWidth={1} dot={false} name="SMA 25" />}
                      {indicators.sma99 && <Line type="monotone" dataKey="sma99" stroke="#d97736" strokeWidth={1} dot={false} name="SMA 99" />}
                      {indicators.ema9 && <Line type="monotone" dataKey="ema9" stroke="#60a5fa" strokeWidth={1} dot={false} name="EMA 9" />}
                      {indicators.ema21 && <Line type="monotone" dataKey="ema21" stroke="#3b82f6" strokeWidth={1} dot={false} name="EMA 21" />}
                      {indicators.ema50 && <Line type="monotone" dataKey="ema50" stroke="#1d4ed8" strokeWidth={1} dot={false} name="EMA 50" />}
                      {indicators.bb && <Line type="monotone" dataKey="bbUpper" stroke="#f472b6" strokeWidth={1} strokeDasharray="3 3" dot={false} name="BB Upper" />}
                      {indicators.bb && <Line type="monotone" dataKey="bbLower" stroke="#f472b6" strokeWidth={1} strokeDasharray="3 3" dot={false} name="BB Lower" />}
                      {/* 🛠️ THÊM MỚI: Vẽ đường Daily VWAP màu Tím Neon sang xịn mịn */}
                      {indicators.vwap && <Line type="monotone" dataKey="vwap" stroke="#c084fc" strokeWidth={2} dot={false} name="Daily VWAP" />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ height: '280px', borderLeft: `1px solid rgba(255,255,255,0.05)` }}>
                  <ResponsiveContainer>
                    <BarChart data={volumeProfileData} layout="vertical" margin={{ top: 5, right: 5, left: -5, bottom: 5 }}>
                      <XAxis type="number" hide />
                      <YAxis dataKey="priceLevel" type="category" tick={{ fill: '#ffffff', fontSize: 9 }} stroke="transparent" />
                      <Tooltip contentStyle={{ background: colors?.bg, fontSize: 11 }} formatter={(v) => v.toLocaleString()} />
                      <Bar dataKey="volume" fill="rgba(244, 114, 182, 0.4)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* CỤM BÊN PHẢI: MA TRẬN LƯỚI HEATMAP 24/7 CO GIÃN THEO NGÀY */}
            <div style={{ border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.15)' }}>
              <div style={{ fontSize: '13px', fontWeight: '900', color: '#fbbf24', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Activity size={16} /> 2. ORDERFLOW HEATMAP DENSITY MATRIX (FIXED 1H INTERVAL)
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '50px repeat(24, 1fr)', gap: '2px', textAlign: 'center', fontSize: '9px', color: colors?.textSub, fontWeight: 'bold' }}>
                  <div></div>
                  {heatmapMatrix?.tradingHours.map(h => <div key={`lbl-hour-${h}`}>{h}h</div>)}
                </div>

                {heatmapMatrix?.daysEn.map((dayEn, dIdx) => (
                  <div key={`grid-row-${dayEn}`} style={{ display: 'grid', gridTemplateColumns: '50px repeat(24, 1fr)', gap: '2px', alignItems: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#fff' }}>{heatmapMatrix.daysVn[dIdx]}</div>
                    
                    {heatmapMatrix.tradingHours.map(hour => {
                      const vol = heatmapMatrix.matrix[dayEn][hour];
                      const ratio = heatmapMatrix.maxVolumeInGrid > 0 ? vol / heatmapMatrix.maxVolumeInGrid : 0;
                      
                      const isZero = vol === 0;
                      const opacityAlpha = isZero ? 0.02 : Math.max(0.12, ratio);

                      let targetColorBase = '245, 158, 11'; 
                      if (matrixType === 'BUY') targetColorBase = '8, 153, 129';  
                      if (matrixType === 'SELL') targetColorBase = '242, 54, 69'; 

                      const cellColor = isZero ? 'rgba(255,255,255,0.02)' : `rgba(${targetColorBase}, ${opacityAlpha})`;

                      return (
                        <div 
                          key={`cell-matrix-${dayEn}-${hour}`}
                          title={`${heatmapMatrix.daysVn[dIdx]} lúc ${hour}:00 | Vol Khớp: ${vol.toLocaleString()}`}
                          style={{ 
                            height: '22px', 
                            backgroundColor: cellColor,
                            borderRadius: '2px',
                            border: (!isZero && vol === heatmapMatrix.maxVolumeInGrid) ? '1px solid #fff' : 'none',
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* 🛠️ CẬP NHẬT: HIỂN THỊ FOOTPRINT CÓ NHÃN TẦNG GIÁ T1 - T5 VÀ BẢNG CHÚ GIẢI */}
          <div style={{ border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '14px', fontWeight: '900', color: '#06b6d4', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Sliders size={16} /> 3. NUMERIC FOOTPRINT CLUSTER MATRIX (CÓ ĐÁNH DẤU TẦNG PHÂN BỔ T1 - T5)
            </div>
            
            {/* BẢNG CHÚ GIẢI (LEGEND NOTE) ĐẦY ĐỦ CÁCH ĐỌC VÀ THỨ TỰ TẦNG GIÁ */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', backgroundColor: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '14px', fontSize: '11px', lineHeight: '1.6' }}>
              <div>
                <strong style={{ color: '#60a5fa', display: 'block', marginBottom: '4px' }}>📌 CẤU TRÚC ĐỌC Ô GIÁ:</strong>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <span style={{ color: '#f23645', fontWeight: 'bold' }}>Số Trái: Bán chủ động (Bid)</span>
                  <span style={{ color: '#089981', fontWeight: 'bold' }}>Số Phải: Mua chủ động (Ask)</span>
                </div>
                <span style={{ color: '#ffffff', opacity: 0.8 }}>Số Giữa (Δ): Delta cục bộ (Lấy Mua - Bán)</span>
              </div>
              <div>
                <strong style={{ color: '#06b6d4', display: 'block', marginBottom: '4px' }}>📊 PHÂN BỔ 5 TẦNG GIÁ TRONG NẾN (MÔ HÌNH HÌNH CHUÔNG):</strong>
                <span style={{ color: '#ffffff', opacity: 0.9 }}>• <b>T5 (Đỉnh nến - 10%)</b> | <b>T4 (Trên thân - 25%)</b> | <b style={{ color: '#fbbf24' }}>T3 (Giữa thân/POC - 40%)</b></span>
                <span style={{ display: 'block', color: '#ffffff', opacity: 0.9 }}>• <b>T2 (Dưới thân - 18%)</b> | <b>T1 (Đáy nến - 7%)</b></span>
              </div>
              <div style={{ gridColumn: '1 / -1', borderTop: '1px dashed rgba(255,255,255,0.05)', paddingTop: '6px' }}>
                <strong style={{ color: '#fbbf24' }}>⚠️ LƯU Ý HIGHLIGHT:</strong> Viền Vàng nguyên cột đại diện cho cây nến có tổng Volume LỚN NHẤT toàn phiên quét (Session POC).
              </div>
            </div>
            
            {/* Thanh cuộn ngang chứa các cột Footprint */}
            <div style={{ overflowX: 'auto', display: 'flex', gap: '14px', paddingBottom: '12px' }} className="matrix-scroll-container">
              {footprintChartData.map((bar, bIdx) => {
                const isDeltaPositive = bar.deltaTotal >= 0;
                const isSessionMaxVol = bar.volume === Math.max(...footprintChartData.map(d => d.volume));
                const showPOC = footprintFilters.highlightPOC && isSessionMaxVol;
                
                return (
                  <div 
                    key={`fp-bar-${bar.ms}-${bIdx}`}
                    style={{ 
                      flexShrink: 0, 
                      width: footprintFilters.splitBidAsk ? '230px' : '140px', // Tăng thêm chiều rộng để chừa chỗ cho nhãn T1-T5
                      backgroundColor: 'rgba(5, 7, 12, 0.8)', 
                      borderRadius: '6px', 
                      padding: '10px 6px', 
                      border: showPOC ? '2px solid #fbbf24' : `1px solid ${isDeltaPositive ? 'rgba(8, 153, 129, 0.4)' : 'rgba(242, 54, 69, 0.4)'}`,
                      textAlign: 'center',
                      boxShadow: showPOC ? '0 0 8px rgba(251, 191, 36, 0.5)' : 'none'
                    }}
                  >
                    {/* Header mốc thời gian */}
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#60a5fa' }}>{bar.timeLabel}</div>
                    <div style={{ fontSize: '11px', color: colors?.textSub, marginBottom: '8px' }}>{bar.dateLabel}</div>

                    {/* VÙNG RUỘT NẾN CẤU TRÚC 5 TẦNG GIÁ KÈM LABEL T1 - T5 */}
                    <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: '4px' }}>
                      {bar.clusters.map((cluster, cIdx) => {
                        const isNếnPOC = bIdx === bar.pocIndex;
                        const isImbalanceBuy = cluster.ask >= cluster.bid * 3;
                        const isImbalanceSell = cluster.bid >= cluster.ask * 3;
                        
                        const showSplit = footprintFilters.splitBidAsk;

                        let cellBg = 'rgba(255,255,255,0.02)';
                        if (footprintFilters.imbalanceBox) {
                          if (isImbalanceBuy) cellBg = 'rgba(8, 153, 129, 0.2)'; 
                          if (isImbalanceSell) cellBg = 'rgba(242, 54, 69, 0.2)'; 
                        }

                        const isEdge = cIdx === 0 || cIdx === 4;
                        const isExhausted = footprintFilters.exhaustionSpot && isEdge && cluster.total < bar.volume * 0.08;
                        
                        // Đặt tên nhãn tầng từ T1 đến T5
                        const tierLabel = `T${cIdx + 1}`;
                        // Tầng 3 mặc định là vùng lõi (POC hình chuông) thì cho màu vàng rực
                        const tierLabelColor = cIdx === 2 ? '#fbbf24' : '#848e9c';

                        return (
                          <div 
                            key={`fp-row-wrapper-${bIdx}-${cIdx}`} 
                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                          >
                            {/* Cột số danh mục tầng (T1 - T5) nằm ở biên trái */}
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: tierLabelColor, width: '18px', textAlign: 'left', opacity: 0.8 }}>
                              {tierLabel}
                            </span>

                            {/* Ô nội dung Footprint chính */}
                            <div
                              key={`fp-cell-${bIdx}-${cIdx}`}
                              title={`Tầng: ${tierLabel} (Giá: ${cluster.priceLevel})\nBuy (Ask): ${Math.round(cluster.ask).toLocaleString()}\nSell (Bid): ${Math.round(cluster.bid).toLocaleString()}\nDelta: ${Math.round(cluster.delta).toLocaleString()}`}
                              style={{
                                backgroundColor: isExhausted ? 'rgba(148, 163, 184, 0.08)' : cellBg,
                                border: (cIdx === 2) ? '1px dashed rgba(251, 191, 36, 0.4)' : '1px solid rgba(255,255,255,0.04)', // Gợi ý nhẹ viền tầng 3 (POC Ruột nến)
                                borderRadius: '4px',
                                padding: '6px 6px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                flex: 1,
                                boxSizing: 'border-box'
                              }}
                            >
                              {showSplit ? (
                                <>
                                  {/* Hàng trên: Bid và Ask */}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '12px', fontWeight: 'bold' }}>
                                    <span style={{ color: isExhausted ? '#64748b' : '#f23645', textDecoration: isImbalanceSell ? 'underline' : 'none' }}>
                                      {Math.round(cluster.bid / 1000)}K
                                    </span>
                                    <span style={{ color: isExhausted ? '#64748b' : '#089981', textDecoration: isImbalanceBuy ? 'underline' : 'none' }}>
                                      {Math.round(cluster.ask / 1000)}K
                                    </span>
                                  </div>
                                  
                                  {/* Hàng dưới: Delta */}
                                  {footprintFilters.deltaLabel && (
                                    <div style={{ fontSize: '11px', fontWeight: '900', color: cluster.delta >= 0 ? '#089981' : '#f23645', opacity: 0.95 }}>
                                      Δ {cluster.delta >= 0 ? '+' : ''}{Math.round(cluster.delta / 1000)}K
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div style={{ width: '100%', textAlign: 'center', fontSize: '13px', fontWeight: '900', color: cluster.delta >= 0 ? '#089981' : '#f23645' }}>
                                  {footprintFilters.deltaLabel ? `Δ ${cluster.delta >= 0 ? '+' : ''}${Math.round(cluster.delta / 1000)}K` : '•'}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Footer Delta tổng của nến */}
                    <div style={{ fontSize: '12px', marginTop: '8px', fontWeight: '900', color: bar.deltaTotal >= 0 ? '#089981' : '#f23645', backgroundColor: 'rgba(255,255,255,0.02)', padding: '2px 0', borderRadius: '3px', marginLeft: '24px' }}>
                      TOTAL Δ {bar.deltaTotal >= 0 ? '+' : ''}{Math.round(bar.deltaTotal / 1000)}K
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* 🛠️ TẦNG TRUNG: NÂNG CẤP CHÈN CLUSTERED BAR CHART (BIỂU ĐỒ CỘT NHÓM SONG SONG THEO GIỜ) */}
          <div style={{ border: `1px solid ${colors?.border || '#2b3139'}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '13px', fontWeight: '900', color: '#06b6d4', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Cpu size={16} /> 3. CLUSTERED ORDERFLOW MOMENTUM: TAKER BUY VS TAKER SELL VOLUME FLOW
            </div>
            <div style={{ width: '100%', height: '220px' }}>
              <ResponsiveContainer>
                {/* Chạy Clustered trên mảng nến visibleChartData để đồng bộ mốc thời gian */}
                <BarChart data={visibleChartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.02)" />
                  <XAxis dataKey="timeLabel" tick={{ fill: colors?.textSub, fontSize: 9 }} />
                  <YAxis tick={{ fill: colors?.textSub, fontSize: 9 }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : (v >= 1000 ? `${(v/1000).toFixed(0)}K` : v)} />
                  <Tooltip contentStyle={{ background: colors?.bg, borderColor: colors?.border, fontSize: 12, color: '#fff' }} formatter={(v) => v.toLocaleString()} />
                  <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
                  
                  {/* Vẽ hai cột Buy và Sell nhóm song song (Clustered) tại từng mốc tọa độ giờ trục X */}
                  <Bar dataKey="takerBuyVol" name="Khối Lượng Mua Chủ Động (Taker Buy)" fill="#089981" barSize={4} />
                  <Bar dataKey="takerSellVol" name="Khối Lượng Bán Chủ Động (Taker Sell)" fill="#f23645" barSize={4} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* TẦNG HẠ: HIỂN THỊ ĐỘNG CÁC ĐỒ THỊ DAO ĐỘNG PHỤ (OSCILLATORS CHART) */}
          {(indicators.rsi || indicators.macd) && (
            <div style={{ display: 'grid', gridTemplateColumns: indicators.rsi && indicators.macd ? '1fr 1fr' : '1fr', gap: '24px' }}>
              
              {/* RSI */}
              {indicators.rsi && (
                <div style={{ border: `1px solid ${colors?.border}`, borderRadius: '6px', padding: '14px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#a3e635', marginBottom: '8px' }}>3. MOMENTUM INDICATOR: RSI (14) OSCILLATOR PROFILE</div>
                  <div style={{ height: '110px' }}>
                    <ResponsiveContainer>
                      <LineChart data={processedChartData} margin={{ top: 5, right: 5, left: -30, bottom: 5 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.02)" />
                        <XAxis dataKey="timeLabel" hide />
                        <YAxis domain={[0, 100]} tick={{ fill: colors?.textSub, fontSize: 9 }} ticks={[30, 50, 70]} />
                        <Tooltip contentStyle={{ background: colors?.bg, fontSize: 11 }} />
                        <Line type="monotone" dataKey="rsi" stroke="#a3e635" strokeWidth={1.5} dot={false} name="RSI" />
                        {/* 🛠️ THÊM MỚI: Vẽ đường ngang nét đứt tại mốc 50, màu xám mờ để đỡ rối mắt */}
                        <ReferenceLine 
                          y={50} 
                          stroke="rgba(255, 255, 255, 0.75)" 
                          strokeDasharray="3 3" 
                          strokeWidth={1}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* MACD */}
              {indicators.macd && (
                <div style={{ border: `1px solid ${colors?.border}`, borderRadius: '6px', padding: '14px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#22c55e', marginBottom: '8px' }}>4. MOMENTUM CONFIRMATION: MACD HISTOGRAM DRIVER</div>
                  <div style={{ height: '110px' }}>
                    <ResponsiveContainer>
                      <BarChart data={processedChartData} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.02)" />
                        <XAxis dataKey="timeLabel" hide />
                        <YAxis tick={{ fill: colors?.textSub, fontSize: 9 }} />
                        <Tooltip contentStyle={{ background: colors?.bg, fontSize: 11 }} />
                        <Bar dataKey="macdHist" fill="#22c55e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      )}

    </div>
  );
}
